import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import { Send, MessageCircle, ChevronRight, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import PageLayout from '../components/PageLayout';

const QAPage = () => {
  const { isAdmin } = useAuth();
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);  // {answer, forwarded, qa_id}
  const [history, setHistory] = useState([]);
  const answerRef = useRef(null);

  const handleAsk = async () => {
    const q = question.trim();
    if (!q) return;
    if (q.length > 500) { toast.error('Question too long (500 chars max)'); return; }

    setLoading(true);
    setResult(null);
    try {
      const res = await api.post('/qa/ask', { question: q });
      const entry = { question: q, ...res.data, timestamp: new Date() };
      setResult(entry);
      setHistory(prev => [entry, ...prev]);
      setQuestion('');
      setTimeout(() => answerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to get answer');
    } finally {
      setLoading(false);
    }
  };

  const EXAMPLE_QUESTIONS = [
    'What vaccinations do I need for Guatemala?',
    'What hotel does the team stay at?',
    'How much is the required donation?',
    'What time does surgery start each morning?',
    'What should I do about currency and ATMs?',
    'What surgical specialties are offered?',
  ];

  return (
    <PageLayout title="Mission Q&A" headerSub="Answers based on the Guatemala Surgery Guide" maxWidth="max-w-2xl">
        <div className="space-y-5">
          {/* Ask box */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-primary" />
                Ask a Question
              </CardTitle>
              <CardDescription>
                Get factual answers about the Guatemala Surgery mission, travel, hotel, health requirements, and more.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                value={question}
                onChange={e => setQuestion(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAsk(); } }}
                placeholder="e.g. What vaccinations do I need before traveling to Guatemala?"
                rows={3}
                className="resize-none"
                maxLength={500}
                data-testid="question-input"
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">{question.length}/500</span>
                <Button
                  onClick={handleAsk}
                  disabled={!question.trim() || loading}
                  className="rounded-full bg-rojo hover:bg-rojo-dark"
                  data-testid="ask-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? 'Thinking...' : 'Ask'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Answer */}
          {result && (
            <Card ref={answerRef} className={result.forwarded ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}>
              <CardContent className="pt-5 pb-5">
                <div className="flex items-start gap-3">
                  {result.forwarded
                    ? <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    : <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />}
                  <div>
                    <p className="text-xs font-semibold text-slate-500 mb-1">Your question: <span className="font-normal italic">{result.question}</span></p>
                    <p className="text-sm text-slate-800 leading-relaxed">{result.answer}</p>
                    {result.forwarded && (
                      <p className="text-xs text-amber-700 mt-2">
                        The admin team will review this and the answer may be added to the knowledge base.
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Example questions */}
          {!result && (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">Common Questions</p>
              <div className="space-y-2">
                {EXAMPLE_QUESTIONS.map(q => (
                  <button
                    key={q}
                    onClick={() => setQuestion(q)}
                    className="w-full text-left flex items-center gap-2 p-3 rounded-xl border border-slate-200 bg-white hover:border-primary/40 hover:bg-primary/2 transition-colors text-sm text-slate-700"
                    data-testid="example-question"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Previous answers */}
          {history.length > 1 && (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">Earlier This Session</p>
              <div className="space-y-2">
                {history.slice(1).map((h, i) => (
                  <div key={i} className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <p className="text-xs text-slate-500 mb-1 italic">Q: {h.question}</p>
                    <p className="text-sm text-slate-700">{h.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
    </PageLayout>
  );
};

export default QAPage;
